import { Flex, Tabs, Box, Text, Checkbox } from '@scale/scaleui-radix';
import {
  WebinarError,
  WebinarErrorMessages,
} from 'react-scale/components/WebinarSessions/constants';
import { UserAutocomplete } from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/UserAutocomplete';
import { WorkerTeamAutocomplete } from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/WorkerTeamAutocomplete';
import { SquadAutocomplete } from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/SquadAutocomplete';
import { ProjectQualificationAutocomplete } from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/ProjectQualificationAutocomplete';
import { SingleAudienceAutoComplete } from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/SingleAudienceAutocomplete';
import { UserIdOrEmailInput } from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/UserIdOrEmailInput';
import { useState } from 'react';
import { ErrorMessage } from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/ErrorMessage';

enum InviteeTabs {
  WORKER_TEAM = 'workerTeam',
  USERS = 'users',
  QUALIFICATION = 'qualification',
  SQUAD = 'squad',
  AUDIENCE = 'audience',
}

const INVITEE_TABS = [
  { id: InviteeTabs.USERS, title: 'Users' },
  { id: InviteeTabs.WORKER_TEAM, title: 'Worker Team' },
  { id: InviteeTabs.QUALIFICATION, title: 'Qualification' },
  { id: InviteeTabs.SQUAD, title: 'Squad' },
  { id: InviteeTabs.AUDIENCE, title: 'Audience' },
];

interface WebinarInviteeFormProps {
  project: string | null;
  invitedWorkerTeams: string[];
  setInvitedWorkerTeams: (invitedWorkerTeams: string[]) => void;
  invitedUsers: string[];
  setInvitedUsers: (invitedUsers: string[]) => void;
  invitedQualifications: string[];
  setInvitedQualifications: (invitedQualifications: string[]) => void;
  invitedSquads: string[];
  setInvitedSquads: (invitedSquads: string[]) => void;
  audience: string | null;
  setAudience: (audience: string | null) => void;
  audienceBucket: number | null;
  setAudienceBucket: (audienceBucket: number | null) => void;
  excludeEmptyQueueUsers: boolean;
  setExcludeEmptyQueueUsers: (excludeEmptyQueueUsers: boolean) => void;
  webinarErrors: WebinarError[];
}

const WebinarInviteeForm = ({
  project,
  invitedWorkerTeams,
  setInvitedWorkerTeams,
  invitedUsers,
  setInvitedUsers,
  invitedQualifications,
  setInvitedQualifications,
  invitedSquads,
  setInvitedSquads,
  audience,
  setAudience,
  audienceBucket,
  setAudienceBucket,
  excludeEmptyQueueUsers,
  setExcludeEmptyQueueUsers,
  webinarErrors,
}: WebinarInviteeFormProps) => {
  const [selectedInviteeTab, setSelectedInviteeTab] = useState<InviteeTabs>(InviteeTabs.USERS);

  return (
    <Flex direction="column" gap="4">
      <Flex direction="column" gap="2">
        <Text as="label" size="2" weight="medium">
          Add Invitees
        </Text>
        <Tabs.Root
          value={selectedInviteeTab}
          onValueChange={(value: string) => setSelectedInviteeTab(value as InviteeTabs)}
        >
          <Tabs.List variant="outline">
            {INVITEE_TABS.map(tab => (
              <Tabs.Trigger key={tab.id} value={tab.id} variant="outline">
                {tab.title}
              </Tabs.Trigger>
            ))}
          </Tabs.List>
          <Box pt="3">
            <Tabs.Content value={InviteeTabs.WORKER_TEAM}>
              <WorkerTeamAutocomplete
                workerTeamIds={invitedWorkerTeams}
                onWorkerTeamIdsChange={setInvitedWorkerTeams}
              />
            </Tabs.Content>
            <Tabs.Content value={InviteeTabs.USERS}>
              <Flex direction="column" gap="2">
                <Flex direction="column" gap="2">
                  <Text as="label" size="2" weight="medium">
                    Search for users
                  </Text>
                  <UserAutocomplete
                    userIds={invitedUsers}
                    onUserIdsChange={setInvitedUsers}
                    showValues={false}
                    limitToScaliens={false}
                    limitToContributors={true}
                  />
                </Flex>
                <Flex direction="column" gap="2">
                  <Text as="label" size="2" weight="medium">
                    Paste in user IDs or emails (must add +worker to username for @scale.com emails)
                  </Text>
                  <UserIdOrEmailInput userIds={invitedUsers} setUsersIds={setInvitedUsers} />
                </Flex>
              </Flex>
            </Tabs.Content>
            <Tabs.Content value={InviteeTabs.QUALIFICATION}>
              {project && project.length > 0 ? (
                <ProjectQualificationAutocomplete
                  projectId={project}
                  qualificationIds={invitedQualifications}
                  onQualificationIdsChange={setInvitedQualifications}
                />
              ) : (
                <Text size="2">
                  Qualifications can only be added if this webinar is associated with a project.
                </Text>
              )}
            </Tabs.Content>
            <Tabs.Content value={InviteeTabs.SQUAD}>
              <SquadAutocomplete squadIds={invitedSquads} onSquadIdsChange={setInvitedSquads} />
            </Tabs.Content>
            <Tabs.Content value={InviteeTabs.AUDIENCE}>
              <SingleAudienceAutoComplete
                audienceId={audience ?? ''}
                onAudienceIdChange={(value: string | null) => {
                  setAudience(value);
                  // Always reset audience bucket when audience is changed
                  setAudienceBucket(null);
                }}
                audienceBucket={audienceBucket}
                setAudienceBucket={setAudienceBucket}
              />
            </Tabs.Content>
          </Box>
        </Tabs.Root>
        {webinarErrors.includes(WebinarError.INVITEES_REQUIRED) && (
          <ErrorMessage text={WebinarErrorMessages[WebinarError.INVITEES_REQUIRED]} />
        )}
        <Text as="label" size="2">
          <Flex as="span" gap="2">
            <Checkbox
              size="2"
              checked={excludeEmptyQueueUsers}
              onCheckedChange={setExcludeEmptyQueueUsers}
            />
            Exclude empty queue users
          </Flex>
        </Text>
      </Flex>
    </Flex>
  );
};

export default WebinarInviteeForm;
